import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  query,
  where,
  getDoc,
  increment,
  runTransaction
} from 'firebase/firestore';
import { db } from './firebase';
import { sendPackagePurchaseNotification } from './whatsappService';

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  PENDING: 'pending'
};

// Create a new subscription or extend existing one
export const createSubscription = async (subscriptionData) => {
  try {
    // Check if user has an active subscription
    const activeSubResult = await getUserActiveSubscription(subscriptionData.userId);

    if (activeSubResult.success && activeSubResult.subscription) {
      // User has active subscription - accumulate benefits
      const existingSub = activeSubResult.subscription;
      const now = new Date();
      const existingEndDate = existingSub.endDate.toDate();

      // Calculate new end date: extend from existing end date (or now if already expired)
      const baseDate = existingEndDate > now ? existingEndDate : now;
      const newEndDate = new Date(baseDate);
      newEndDate.setDate(newEndDate.getDate() + subscriptionData.duration);

      // Accumulate all limits (applications for job seekers, postings for employers)
      const currentMaxApp = existingSub.maxApplications || 0;
      const addingMaxApp = subscriptionData.maxApplications || 0;
      const updatedMaxApplications = currentMaxApp + addingMaxApp;

      const currentMaxPostings = existingSub.maxJobPostings || 0;
      const addingMaxPostings = subscriptionData.maxJobPostings || 0;
      const updatedMaxJobPostings = currentMaxPostings + addingMaxPostings;

      const currentFeatured = existingSub.featuredJobs || 0;
      const addingFeatured = subscriptionData.featuredJobs || 0;
      const updatedFeaturedJobs = currentFeatured + addingFeatured;

      const currentTemplates = existingSub.resumeTemplates || 0;
      const addingTemplates = subscriptionData.resumeTemplates || 0;
      const updatedResumeTemplates = currentTemplates + addingTemplates;

      console.log('🔄 UPGRADING SUBSCRIPTION LIMITS:');
      console.log('--------------------------------------------------');
      console.log(`📝 Job Applications:  ${currentMaxApp} (Existing) + ${addingMaxApp} (New) = ${updatedMaxApplications} (Total)`);
      console.log(`📢 Job Postings:      ${currentMaxPostings} (Existing) + ${addingMaxPostings} (New) = ${updatedMaxJobPostings} (Total)`);
      console.log(`⭐ Featured Jobs:     ${currentFeatured} (Existing) + ${addingFeatured} (New) = ${updatedFeaturedJobs} (Total)`);
      console.log(`📄 Resume Templates:  ${currentTemplates} (Existing) + ${addingTemplates} (New) = ${updatedResumeTemplates} (Total)`);
      console.log('--------------------------------------------------');

      // Update existing subscription with accumulated values
      const updateData = {
        maxApplications: updatedMaxApplications,
        maxJobPostings: updatedMaxJobPostings,
        featuredJobs: updatedFeaturedJobs,
        resumeTemplates: updatedResumeTemplates,
        profileBoost: subscriptionData.profileBoost || existingSub.profileBoost || false,
        candidateSearch: subscriptionData.candidateSearch || existingSub.candidateSearch || false,
        analyticsAccess: subscriptionData.analyticsAccess || existingSub.analyticsAccess || false,
        endDate: Timestamp.fromDate(newEndDate),
        updatedAt: Timestamp.now(),
        status: SUBSCRIPTION_STATUS.ACTIVE, // Ensure status is active
        // Keep track of purchase history
        purchaseHistory: [
          ...(existingSub.purchaseHistory || []),
          {
            packageId: subscriptionData.packageId || 'unknown',
            packageName: subscriptionData.packageName || 'Unknown Package',
            price: subscriptionData.price || 0,
            duration: subscriptionData.duration || 0,
            maxApplications: subscriptionData.maxApplications || 0,
            maxJobPostings: subscriptionData.maxJobPostings || 0,
            featuredJobs: subscriptionData.featuredJobs || 0,
            resumeTemplates: subscriptionData.resumeTemplates || 0,
            purchasedAt: Timestamp.now(),
            paymentId: subscriptionData.paymentId || null,
            razorpayPaymentId: subscriptionData.razorpayPaymentId || null,
            cashfreePaymentId: subscriptionData.cashfreePaymentId || null,
            cashfreeOrderId: subscriptionData.cashfreeOrderId || null
          }
        ]
      };

      console.log('📝 [v3-Fix] Updating subscription with safe defaults:', JSON.stringify(updateData, null, 2));
      const updateResult = await updateSubscription(existingSub.id, updateData);

      if (!updateResult.success) {
        console.error('❌ Failed to update subscription:', updateResult.error);
        return { success: false, error: 'Database update failed: ' + updateResult.error };
      }

      console.log(`✅ Extended existing subscription: +${subscriptionData.maxApplications || 0} applications, +${subscriptionData.maxJobPostings || 0} job postings, +${subscriptionData.duration} days`);
      console.log(`📊 New totals: ${updatedMaxApplications} applications, ${updatedMaxJobPostings} job postings, valid until ${newEndDate.toLocaleDateString()}`);

      // Update user document in Firestore to persist payment status
      try {
        const userRef = doc(db, 'users', subscriptionData.userId);
        await updateDoc(userRef, {
          paymentId: subscriptionData.paymentId || true
        });
        console.log('✅ Persisted user paymentId on subscription upgrade');
      } catch (userUpdateError) {
        console.warn('⚠️ Failed to update user paymentId:', userUpdateError);
      }

      return {
        success: true,
        id: existingSub.id,
        subscription: { ...existingSub, ...updateData },
        extended: true
      };
    }

    // No active subscription - create new one
    const startDate = Timestamp.now();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + subscriptionData.duration);

    const newSubscription = {
      ...subscriptionData,
      startDate,
      endDate: Timestamp.fromDate(endDate),
      createdAt: startDate,
      updatedAt: startDate,
      status: SUBSCRIPTION_STATUS.ACTIVE,
      usageStats: {
        applicationsUsed: 0,
        jobPostingsUsed: 0,
        featuredJobsUsed: 0,
        resumeDownloads: 0
      },
      purchaseHistory: [
        {
          packageId: subscriptionData.packageId,
          packageName: subscriptionData.packageName,
          price: subscriptionData.price,
          duration: subscriptionData.duration,
          maxApplications: subscriptionData.maxApplications,
          maxJobPostings: subscriptionData.maxJobPostings,
          featuredJobs: subscriptionData.featuredJobs,
          purchasedAt: startDate,
          paymentId: subscriptionData.paymentId,
          razorpayPaymentId: subscriptionData.razorpayPaymentId || null,
          cashfreePaymentId: subscriptionData.cashfreePaymentId || null,
          cashfreeOrderId: subscriptionData.cashfreeOrderId || null
        }
      ]
    };

    const docRef = await addDoc(collection(db, 'subscriptions'), newSubscription);
    console.log(`✅ Created new subscription: ${subscriptionData.maxJobPostings} jobs, ${subscriptionData.duration} days`);

    // Update user document in Firestore to persist payment status
    try {
      const userRef = doc(db, 'users', subscriptionData.userId);
      await updateDoc(userRef, {
        paymentId: subscriptionData.paymentId || true
      });
      console.log('✅ Persisted user paymentId on new subscription');
    } catch (userUpdateError) {
      console.warn('⚠️ Failed to update user paymentId:', userUpdateError);
    }

    // Send WhatsApp notification to user about package purchase
    try {
      if (subscriptionData.userPhone && subscriptionData.userName) {
        await sendPackagePurchaseNotification({
          phoneNumber: subscriptionData.userPhone,
          userName: subscriptionData.userName,
          packageName: subscriptionData.packageName,
          purchaseDate: new Date().toLocaleDateString('en-IN')
        });
      }
    } catch (whatsappError) {
      // Don't fail subscription if WhatsApp fails
      console.error('WhatsApp notification failed:', whatsappError);
    }

    return { success: true, id: docRef.id, subscription: newSubscription, extended: false };
  } catch (error) {
    console.error('Error creating/extending subscription:', error);
    return { success: false, error: error.message };
  }
};

// Get user's active subscription
export const getUserActiveSubscription = async (userId) => {
  try {
    const q = query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId),
      where('status', '==', SUBSCRIPTION_STATUS.ACTIVE)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      // Sort in memory to get the most recent
      const sortedDocs = querySnapshot.docs.sort((a, b) => {
        const aTime = a.data().createdAt?.toMillis() || 0;
        const bTime = b.data().createdAt?.toMillis() || 0;
        return bTime - aTime;
      });
      const doc = sortedDocs[0];
      const subscription = {
        id: doc.id,
        ...doc.data()
      };

      // Helper to extract numeric limits from features if needed
      const extractLimitFromFeatures = (features, pattern) => {
        if (!features || !Array.isArray(features)) return 0;
        for (const feature of features) {
          const match = feature.match(pattern);
          if (match && match[1]) {
            return parseInt(match[1], 10);
          }
        }
        return 0;
      };

      // Ensure limits are populated even if missing in DB (legacy data fix)
      if (!subscription.maxJobPostings && subscription.features) {
        subscription.maxJobPostings = extractLimitFromFeatures(subscription.features, /(\d+)\s+Job Post/i);
      }
      if (!subscription.maxApplications && subscription.features) {
        subscription.maxApplications = extractLimitFromFeatures(subscription.features, /(\d+)\s+Job Application/i);
      }

      // Check if subscription has expired
      const now = new Date();
      const endDate = subscription.endDate.toDate();

      if (now > endDate) {
        // Mark as expired
        await updateSubscription(subscription.id, { status: SUBSCRIPTION_STATUS.EXPIRED });
        return { success: true, subscription: null, expired: true };
      }

      return { success: true, subscription };
    }

    return { success: true, subscription: null };
  } catch (error) {
    console.error('Error fetching user subscription:', error);
    return { success: false, error: error.message };
  }
};

// Get all user subscriptions (history)
export const getUserSubscriptions = async (userId) => {
  try {
    const q = query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const subscriptions = [];

    querySnapshot.forEach((doc) => {
      subscriptions.push({
        id: doc.id,
        ...doc.data()
      });
    });

    // Sort in memory by createdAt (newest first)
    subscriptions.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return { success: true, subscriptions };
  } catch (error) {
    console.error('Error fetching user subscriptions:', error);
    return { success: false, error: error.message };
  }
};

// Update subscription
export const updateSubscription = async (subscriptionId, updateData) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    await updateDoc(subscriptionRef, {
      ...updateData,
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating subscription:', error);
    return { success: false, error: error.message };
  }
};

// Cancel subscription
export const cancelSubscription = async (subscriptionId) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    await updateDoc(subscriptionRef, {
      status: SUBSCRIPTION_STATUS.CANCELLED,
      cancelledAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    return { success: false, error: error.message };
  }
};

// Update usage stats with atomic increment
export const updateUsageStats = async (subscriptionId, usageType, incrementValue = 1) => {
  try {
    console.log(`📊 [updateUsageStats] Updating ${usageType} for subscription ${subscriptionId} by ${incrementValue}`);

    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    const subscriptionDoc = await getDoc(subscriptionRef);

    if (!subscriptionDoc.exists()) {
      console.error('❌ [updateUsageStats] Subscription not found:', subscriptionId);
      return { success: false, error: 'Subscription not found' };
    }

    // Use atomic increment to prevent race conditions
    const updateData = {
      [`usageStats.${usageType}`]: increment(incrementValue),
      updatedAt: Timestamp.now()
    };

    await updateDoc(subscriptionRef, updateData);

    // Get updated stats
    const updatedDoc = await getDoc(subscriptionRef);
    const newStats = updatedDoc.data().usageStats || {};

    console.log(`✅ [updateUsageStats] Updated successfully. New stats:`, newStats);
    return { success: true, newStats };
  } catch (error) {
    console.error('❌ [updateUsageStats] Error updating usage stats:', error);
    return { success: false, error: error.message };
  }
};

// Decrement usage stats (for refunds or cancellations)
export const decrementUsageStats = async (subscriptionId, usageType, decrementValue = 1) => {
  try {
    console.log(`📉 [decrementUsageStats] Decrementing ${usageType} for subscription ${subscriptionId} by ${decrementValue}`);

    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    const subscriptionDoc = await getDoc(subscriptionRef);

    if (!subscriptionDoc.exists()) {
      return { success: false, error: 'Subscription not found' };
    }

    // Use atomic increment with negative value
    const updateData = {
      [`usageStats.${usageType}`]: increment(-decrementValue),
      updatedAt: Timestamp.now()
    };

    await updateDoc(subscriptionRef, updateData);

    // Get updated stats
    const updatedDoc = await getDoc(subscriptionRef);
    const newStats = updatedDoc.data().usageStats || {};

    console.log(`✅ [decrementUsageStats] Decremented successfully. New stats:`, newStats);
    return { success: true, newStats };
  } catch (error) {
    console.error('❌ [decrementUsageStats] Error:', error);
    return { success: false, error: error.message };
  }
};

// Check if user can perform action based on package limits
export const checkPackageLimit = async (userId, actionType) => {
  try {
    console.log(`🔍 [checkPackageLimit] Checking ${actionType} limit for user:`, userId);

    const result = await getUserActiveSubscription(userId);

    if (!result.success) {
      console.error('❌ [checkPackageLimit] Failed to get subscription:', result.error);
      return { success: false, error: result.error };
    }

    if (!result.subscription) {
      console.warn('⚠️ [checkPackageLimit] No active subscription found');
      return {
        success: false,
        error: 'No active subscription. Please purchase a package to continue.',
        needsSubscription: true
      };
    }

    const subscription = result.subscription;
    const usageStats = subscription.usageStats || {
      applicationsUsed: 0,
      jobPostingsUsed: 0,
      featuredJobsUsed: 0,
      resumeDownloads: 0
    };

    console.log('📊 [checkPackageLimit] Current usage stats:', usageStats);
    console.log('📦 [checkPackageLimit] Package limits:', {
      maxApplications: subscription.maxApplications,
      maxJobPostings: subscription.maxJobPostings,
      featuredJobs: subscription.featuredJobs
    });

    // Check limits based on action type
    let limitInfo = {};

    switch (actionType) {
      case 'application':
        const maxApps = subscription.maxApplications || 0;
        const usedApps = usageStats.applicationsUsed || 0;

        if (maxApps > 0 && usedApps >= maxApps) {
          console.warn(`⚠️ [checkPackageLimit] Application limit reached: ${usedApps}/${maxApps}`);
          return {
            success: false,
            error: 'Application limit reached',
            limitReached: true,
            current: usedApps,
            max: maxApps,
            remaining: 0
          };
        }

        limitInfo = {
          current: usedApps,
          max: maxApps,
          remaining: maxApps - usedApps
        };
        break;

      case 'jobPosting':
        const maxJobs = subscription.maxJobPostings || 0;
        const usedJobs = usageStats.jobPostingsUsed || 0;

        if (maxJobs > 0 && usedJobs >= maxJobs) {
          console.warn(`⚠️ [checkPackageLimit] Job posting limit reached: ${usedJobs}/${maxJobs}`);
          return {
            success: false,
            error: 'Job posting limit reached',
            limitReached: true,
            current: usedJobs,
            max: maxJobs,
            remaining: 0
          };
        }

        limitInfo = {
          current: usedJobs,
          max: maxJobs,
          remaining: maxJobs - usedJobs
        };
        break;

      case 'featuredJob':
        const maxFeatured = subscription.featuredJobs || 0;
        const usedFeatured = usageStats.featuredJobsUsed || 0;

        if (maxFeatured > 0 && usedFeatured >= maxFeatured) {
          console.warn(`⚠️ [checkPackageLimit] Featured job limit reached: ${usedFeatured}/${maxFeatured}`);
          return {
            success: false,
            error: 'Featured job limit reached',
            limitReached: true,
            current: usedFeatured,
            max: maxFeatured,
            remaining: 0
          };
        }

        limitInfo = {
          current: usedFeatured,
          max: maxFeatured,
          remaining: maxFeatured - usedFeatured
        };
        break;

      default:
        console.warn('⚠️ [checkPackageLimit] Unknown action type:', actionType);
        break;
    }

    console.log(`✅ [checkPackageLimit] Limit check passed for ${actionType}:`, limitInfo);
    return {
      success: true,
      subscription,
      canProceed: true,
      ...limitInfo
    };
  } catch (error) {
    console.error('❌ [checkPackageLimit] Error checking package limit:', error);
    return { success: false, error: error.message };
  }
};

// Get subscription analytics for admin
export const getSubscriptionAnalytics = async () => {
  try {
    const subscriptionsSnapshot = await getDocs(collection(db, 'subscriptions'));

    const analytics = {
      totalSubscriptions: subscriptionsSnapshot.size,
      activeSubscriptions: 0,
      expiredSubscriptions: 0,
      cancelledSubscriptions: 0,
      revenue: 0,
      packageBreakdown: {},
      userTypeBreakdown: {}
    };

    subscriptionsSnapshot.forEach((doc) => {
      const subscription = doc.data();

      // Count by status
      switch (subscription.status) {
        case SUBSCRIPTION_STATUS.ACTIVE:
          analytics.activeSubscriptions++;
          break;
        case SUBSCRIPTION_STATUS.EXPIRED:
          analytics.expiredSubscriptions++;
          break;
        case SUBSCRIPTION_STATUS.CANCELLED:
          analytics.cancelledSubscriptions++;
          break;
      }

      // Calculate revenue
      analytics.revenue += subscription.price || 0;

      // Package breakdown
      const packageName = subscription.packageName || 'Unknown';
      analytics.packageBreakdown[packageName] = (analytics.packageBreakdown[packageName] || 0) + 1;

      // User type breakdown
      const userType = subscription.userType || 'Unknown';
      analytics.userTypeBreakdown[userType] = (analytics.userTypeBreakdown[userType] || 0) + 1;
    });

    return { success: true, analytics };
  } catch (error) {
    console.error('Error fetching subscription analytics:', error);
    return { success: false, error: error.message };
  }
};

// Renew subscription
export const renewSubscription = async (userId, packageData) => {
  try {
    // Cancel current active subscription
    const currentResult = await getUserActiveSubscription(userId);
    if (currentResult.success && currentResult.subscription) {
      await cancelSubscription(currentResult.subscription.id);
    }

    // Create new subscription
    const newSubscriptionData = {
      userId,
      packageId: packageData.id,
      packageName: packageData.name,
      packageType: packageData.type,
      price: packageData.price,
      duration: packageData.duration,
      features: packageData.features,
      maxApplications: packageData.maxApplications,
      maxJobPostings: packageData.maxJobPostings,
      featuredJobs: packageData.featuredJobs,
      resumeTemplates: packageData.resumeTemplates,
      profileBoost: packageData.profileBoost,
      candidateSearch: packageData.candidateSearch,
      analyticsAccess: packageData.analyticsAccess,
      userType: packageData.type
    };

    return await createSubscription(newSubscriptionData);
  } catch (error) {
    console.error('Error renewing subscription:', error);
    return { success: false, error: error.message };
  }
};
